import React, { useMemo } from "react";
import { X, AlertCircle, Leaf } from "lucide-react";
import { formatMoney, cleanImageUrl, dietaryBadgesFor } from "../../../lib/retailFormat";

// Phase 3L.1 Pass B — dynamic product info modal.
//
// Renders every real piece of product data we have, with no hardcoded
// whitelist. Strategy:
//   1) Universal known fields (description, category, sku, unit, …) when
//      present and non-empty.
//   2) Every additional key in `custom_fields` / `extra` that holds data,
//      rendered with a humanised label. We never invent missing data.
//
// Empty / null / "" / empty-array values are skipped so the modal never
// looks padded with hollow rows.

// Keys whose presentation is special (image, badges, price, availability)
// are handled outside the dynamic field loop so they don't leak into the
// row grid as raw strings.
const SPECIAL_KEYS = new Set([
  "image_url", "image", "img", "_img",
  "is_vegetarian", "is_vegan", "is_gluten_free", "is_glutenfree",
  "is_dairy_free", "is_dairyfree", "is_nut_free",
  "is_halal", "is_kosher", "is_spicy",
]);

// Universal known fields rendered first (when present). Order matters —
// these are the rows operators rely on most.
const PRIMARY_FIELDS = [
  ["category",         "Category"],
  ["description",      "Description"],
  ["unit",             "Unit"],
  ["sku",              "SKU"],
];

function humanise(key) {
  return String(key)
    .replace(/^is_/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isEmpty(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
}

function formatValue(v) {
  if (Array.isArray(v)) return v.filter((x) => x !== null && x !== undefined && x !== "").join(", ");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export default function ProductInfoModal({ product, currency, onClose }) {
  const img = product._img || cleanImageUrl(product.image_url);
  const badges = useMemo(() => dietaryBadgesFor(product), [product]);

  // Flatten custom_fields + extra into one bag, lower-cased keys for
  // dedupe but original keys preserved for the label.
  const customBag = useMemo(() => {
    const out = {};
    const src = { ...(product.custom_fields || {}), ...(product.extra || {}) };
    for (const [k, v] of Object.entries(src)) {
      if (k.startsWith("_")) continue;
      if (SPECIAL_KEYS.has(String(k).toLowerCase())) continue;
      out[k] = v;
    }
    return out;
  }, [product]);

  // Primary rows: universal fields the storefront ships with.
  const primaryRows = PRIMARY_FIELDS
    .map(([key, label]) => [label, product[key]])
    .filter(([, v]) => !isEmpty(v));

  // Custom rows: everything else from custom_fields/extra.
  const customRows = Object.entries(customBag)
    .filter(([, v]) => !isEmpty(v))
    .map(([k, v]) => [humanise(k), formatValue(v)]);

  // Allergens + Ingredients: tolerate either a top-level array OR a
  // custom_field string. Show them as their own visually-distinct block
  // because customers scan for these first.
  const allergens = product.allergens && product.allergens.length
    ? product.allergens
    : (customBag.allergens && (Array.isArray(customBag.allergens) ? customBag.allergens : String(customBag.allergens).split(",").map((s) => s.trim()).filter(Boolean)));
  const ingredients = product.ingredients && product.ingredients.length
    ? product.ingredients
    : (customBag.ingredients && (Array.isArray(customBag.ingredients) ? customBag.ingredients : String(customBag.ingredients).split(",").map((s) => s.trim()).filter(Boolean)));
  // Pull these out of the customRows so they don't render twice.
  const filteredCustomRows = customRows.filter(([label]) => {
    const lk = label.toLowerCase();
    return lk !== "allergens" && lk !== "ingredients";
  });

  const hasAnyDetail = primaryRows.length > 0 || filteredCustomRows.length > 0 || (allergens && allergens.length) || (ingredients && ingredients.length);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4 py-8 bg-black/60 overflow-y-auto" data-testid="retail-info-modal">
      <div className="relative w-full max-w-md bg-white rounded-2xl text-stone-900 shadow-2xl overflow-hidden my-auto">
        <button
          onClick={onClose}
          data-testid="retail-info-close"
          aria-label="Close"
          className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/90 hover:bg-white shadow"
        >
          <X className="w-4 h-4"/>
        </button>

        {img && (
          <img
            src={img}
            alt={product.name}
            className="w-full h-44 object-cover bg-stone-100"
            loading="lazy"
            data-testid="retail-info-image"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}

        <div className="p-5 max-h-[70vh] overflow-y-auto">
          <h3 className="font-serif text-2xl leading-tight">{product.name}</h3>

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <div className="text-lg font-semibold text-stone-900" data-testid="retail-info-price">
              {formatMoney(product.price, currency)}
            </div>
            {badges.length > 0 && (
              <div className="flex gap-1 flex-wrap" data-testid="retail-info-dietary">
                {badges.map((b) => (
                  <span key={b.k} className={"text-[10px] font-bold px-1.5 py-0.5 rounded " + b.cls} title={b.title}>
                    {b.k}
                  </span>
                ))}
              </div>
            )}
            {product.available === false && (
              <span className="ml-auto text-[11px] px-2 py-0.5 rounded bg-stone-200 text-stone-500" data-testid="retail-info-unavailable">
                Unavailable
              </span>
            )}
          </div>

          {/* Ingredients pill list — only when present */}
          {ingredients && ingredients.length > 0 && (
            <section className="mt-4" data-testid="retail-info-ingredients">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 flex items-center gap-1.5">
                <Leaf className="w-3 h-3" /> Ingredients
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ingredients.map((i, idx) => (
                  <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {String(i)}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Allergens highlighted in red so customers can spot them */}
          {allergens && allergens.length > 0 && (
            <section className="mt-4" data-testid="retail-info-allergens">
              <div className="text-[10px] uppercase tracking-widest text-red-600 mb-1.5 flex items-center gap-1.5">
                <AlertCircle className="w-3 h-3" /> Allergens
              </div>
              <div className="flex flex-wrap gap-1.5">
                {allergens.map((a, idx) => (
                  <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">
                    {String(a)}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Primary fields */}
          {primaryRows.length > 0 && (
            <dl className="mt-4 divide-y divide-stone-100 text-sm" data-testid="retail-info-primary">
              {primaryRows.map(([label, value]) => (
                <Row key={label} label={label} value={String(value)} />
              ))}
            </dl>
          )}

          {/* Dynamic custom rows from custom_fields / extra */}
          {filteredCustomRows.length > 0 && (
            <dl className="mt-2 divide-y divide-stone-100 text-sm" data-testid="retail-info-custom">
              {filteredCustomRows.map(([label, value]) => (
                <Row key={label} label={label} value={value} />
              ))}
            </dl>
          )}

          {!hasAnyDetail && (
            <p className="mt-3 text-sm text-stone-500 italic" data-testid="retail-info-empty">
              No additional details available for this item.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="py-2 grid grid-cols-3 gap-2">
      <dt className="text-stone-500 uppercase text-[10px] tracking-wider self-center">{label}</dt>
      <dd className="col-span-2 text-stone-800 whitespace-pre-wrap break-words">{value}</dd>
    </div>
  );
}
